import { ChangeEvent, KeyboardEvent } from 'react'
import Input from './Input'
import { FOCUS_VISIBLE_RING } from '../../../lib/a11yFocus'

export type SearchInputProps = {
	name: string
	label?: string
	placeholder?: string
	value: string
	onChange: (event: ChangeEvent<HTMLInputElement>) => void
	onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
	onClear?: () => void
	onSearch?: () => void
	isLoading?: boolean
	className?: string
	disabled?: boolean
	size?: 'sm' | 'md'
}

function SearchInput({
	name,
	label,
	placeholder,
	value,
	onChange,
	onKeyDown,
	onClear,
	onSearch,
	isLoading = false,
	className = '',
	disabled = false,
	size = 'sm',
}: SearchInputProps) {
	const hasValue = value.length > 0
	const canClear = hasValue && !!onClear

	const icon = isLoading
		? 'fa-solid fa-spinner fa-spin'
		: canClear
		? 'fa-solid fa-xmark'
		: 'fa-solid fa-magnifying-glass'

	const accessibilityLabel = isLoading
		? 'Searching'
		: canClear
		? 'Clear search'
		: 'Search'

	const handleButtonClick = () => {
		if (canClear) {
			onClear()
		} else if (onSearch) {
			onSearch()
		}
	}

	return (
		<div className={className}>
			{label && (
				<label htmlFor={name} className='block text-sm text-charcoal-100 my-1'>
					{label}
				</label>
			)}
			<div className='relative'>
				<Input
					type='text'
					name={name}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					onKeyDown={onKeyDown}
					disabled={disabled}
					additionalClass={size === 'md' ? 'h-12' : 'h-[42px] min-h-[42px]'}
					inputAdditionalClass={size === 'md' ? 'pr-12' : 'pr-11'}
				/>
				{/* Vertical separator before button */}
				<div
					className='absolute top-2 bottom-2 w-px bg-charcoal-100 pointer-events-none'
					style={{ right: '40px' }}
				/>
				<button
					type='button'
					tabIndex={-1}
					onClick={handleButtonClick}
					disabled={disabled || isLoading}
					aria-label={accessibilityLabel}
					className={`absolute top-1/2 transform -translate-y-1/2 right-1 inline-flex items-center justify-center rounded text-charcoal-500 hover:bg-algae-050 disabled:bg-transparent disabled:text-charcoal-300 ${FOCUS_VISIBLE_RING} ${size === 'md' ? 'h-10 w-10' : 'h-9 w-9'}`}
					style={{ right: '2.5px' }}
				>
					<i className={`${icon}`} aria-hidden='true' />
				</button>
			</div>
		</div>
	)
}

export default SearchInput
